import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import {
  FILE_PACKAGE_REFRESH_MS,
  FILE_PACKAGES_QUERY_KEY,
  getFilePackageStatuses,
} from '@/services/filePackageService';
import { useFilePackageStore } from '@/stores/filePackageStore';
import { useUserStore } from '@/stores/user';
import type { FilePackageStatusItem } from '@/types/filePackage.types';

export const filePackageReadyMessage = (packageName: string) =>
  `${packageName} is ready for export on the downloads page.`;
export const filePackageFailedMessage = (packageName: string) => `${packageName} could not be generated.`;

// After a failed status request, keep trying, but less often.
export const FILE_PACKAGE_RETRY_MS = 30000;

interface StatusSnapshot {
  ids: string[];
  statuses: FilePackageStatusItem[];
}

/**
 * Polls the packages this user generated (tracked in `useFilePackageStore`)
 * and announces each one once it is Ready or Failed. Mounted once in Template,
 * so it runs on every page; it makes no requests while nothing is pending.
 */
export const useFilePackageStatusPolling = () => {
  const pendingIds = useFilePackageStore((state) => state.pendingIds);
  const untrack = useFilePackageStore((state) => state.untrack);
  const resetTracking = useFilePackageStore((state) => state.reset);
  const isLoggedIn = useUserStore((state) => !!state.accessToken);
  const queryClient = useQueryClient();

  // Every logout path (menu, inactivity, failed token refresh) clears the
  // access token; drop tracked packages so the next user isn't told about them.
  useEffect(() => {
    if (!isLoggedIn) resetTracking();
  }, [isLoggedIn, resetTracking]);

  const { data } = useQuery<StatusSnapshot>({
    // Own key, so invalidating the Downloads list below doesn't refetch this.
    queryKey: ['filePackageStatus', pendingIds],
    queryFn: async () => {
      const { data: statuses, error } = await getFilePackageStatuses(pendingIds);
      if (error) throw error;
      // Keep the ids this response answers, so a package tracked meanwhile
      // isn't mistaken for a deleted one.
      return { ids: pendingIds, statuses: statuses ?? [] };
    },
    enabled: isLoggedIn && pendingIds.length > 0,
    refetchInterval: (query) => (query.state.error ? FILE_PACKAGE_RETRY_MS : FILE_PACKAGE_REFRESH_MS),
  });

  useEffect(() => {
    if (!data) return;
    const finished = data.statuses.filter((item) => item.status !== 'IN_PROGRESS');
    // Ids the API no longer knows (e.g. deleted) are dropped silently.
    const missing = data.ids.filter((id) => !data.statuses.some((item) => item.filePackageId === id));
    const done = [...finished.map((item) => item.filePackageId), ...missing].filter((id) =>
      useFilePackageStore.getState().pendingIds.includes(id)
    );
    if (done.length === 0) return;

    finished
      .filter((item) => done.includes(item.filePackageId))
      .forEach((item) =>
        item.status === 'READY'
          ? toast({ description: filePackageReadyMessage(item.packageName), duration: 8000 })
          : toast({ description: filePackageFailedMessage(item.packageName), variant: 'destructive', duration: 8000 })
      );
    untrack(done);
    void queryClient.invalidateQueries({ queryKey: [FILE_PACKAGES_QUERY_KEY] });
  }, [data, untrack, queryClient]);
};
